import firebase from 'firebase/compat/app'
import 'firebase/compat/firestore'
import dayjs from 'dayjs'
const db = firebase.firestore()

const state = {
  applicantPaymentOptions: [
    { text: 'Pending payment', value: false },
    { text: 'Paid', value: true }
  ],
  applicants: [],
  applicant: null
}

const actions = {
  GET_APPLICANT ({ commit, state, rootState }, payload) {
    let resolved = false
    return new Promise((resolve, reject) => {
      const user = rootState.auth.user
      // Block managers from fetching applicants they are not assigned to
      if (user && user.isManager && !user.isAdmin) {
        // Pre-check: fetch once and verify assignment before subscribing
        db.collection('applicants').doc(payload.id).get().then(doc => {
          if (!doc.exists) {
            return reject(new Error('Applicant not found'))
          }
          const data = doc.data()
          if (!data.availableTo || !data.availableTo.includes(user.firstName.toLowerCase())) {
            const err = new Error('Permission denied')
            err.code = 'permission-denied'
            return reject(err)
          }
          // Assignment confirmed — subscribe to updates
          commit('SET_APPLICANT', data)
          const unsub = db.collection('applicants').doc(payload.id).onSnapshot(doc => {
            if (doc.exists) {
              commit('SET_APPLICANT', doc.data())
            }
          }, (error) => {
            reject(error)
          })
          resolve(unsub)
        }).catch(reject)
        return
      }
      const ref = db.collection('applicants').doc(payload.id)
      const unsub = ref.onSnapshot(doc => {
        if (doc.exists) {
          commit('SET_APPLICANT', doc.data())
        }
        if (!resolved) {
          resolved = true
          resolve(unsub)
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  async GET_APPLICANTS ({ commit, rootState }) {
    try {
      const user = rootState.auth.user
      let ref = db.collection('applicants')

      // Managers only get applicants assigned to them
      if (user && user.isManager && !user.isAdmin) {
        ref = ref.where('availableTo', 'array-contains', user.firstName.toLowerCase())
      }

      const snapshot = await ref.get()
      const applicants = snapshot.docs.map(doc => doc.data())
      commit('SET_APPLICANTS', applicants)
      const unsub = ref.onSnapshot(snapshot => {
        const applicants = snapshot.docs.map(doc => doc.data())
        commit('SET_APPLICANTS', applicants)
      })
      return unsub
    } catch (error) {
      console.error(error)
      throw error
    }
  },
  CREATE_APPLICANT ({ commit, rootState }, payload) {
    const user = rootState.auth && rootState.auth.user
    if (!user || !user.isAdmin) {
      return Promise.reject(new Error('Permission denied: admin only'))
    }
    const data = payload.applicant
    return db.collection('applicants')
      .add(data)
      .then(snapshot => {
        data.id = snapshot.id
        return db.collection('applicants')
          .doc(snapshot.id)
          .update(data)
          .then(() => {
            // get the applicant's body and copy requirements
            return db.collection('bodies')
              .doc(data.endorsingBody)
              .get()
          })
          .then((doc) => {
            if (doc.exists) {
              const body = doc.data()
              const requirements = body.requirements || []
              data.requirements = requirements.filter((requirement) => {
                return requirement.type === data.type
              })
              return db.collection('applicants')
                .doc(data.id)
                .update({ requirements: data.requirements })
            }
          })
      })
  },
  UPDATE_APPLICANT ({ commit, rootState }, payload) {
    const user = rootState.auth && rootState.auth.user
    if (!user || (!user.isAdmin && !user.isManager)) {
      return Promise.reject(new Error('Permission denied'))
    }
    const data = payload
    return db.collection('applicants')
      .doc(data.id)
      .update({
        firstName: data.firstName,
        lastName: data.lastName,
        createdAt: data.createdAt,
        isActive: data.isActive,
        paid: data.paid,
        description: data.description,
        shortDescription: data.shortDescription,
        type: data.type,
        endorsingBody: data.endorsingBody,
        deadline: data.deadline,
        updatedAt: dayjs().format(),
        availableTo: data.availableTo,
        picture: data.picture,
        linkedin: data.linkedin,
        keyDates: data.keyDates || []
      })
  },
  UPDATE_APPLICANT_REQUIREMENTS ({ commit }, payload) {
    const data = payload
    return db.collection('applicants')
      .doc(data.applicantId)
      .update({
        requirements: data.requirements,
        updatedAt: dayjs().format()
      })
  },
  UPDATE_APPLICANT_PUBLIC_REQUIREMENTS ({ commit }, payload) {
    const data = payload
    const requirements = data.requirements.map((requirement) => {
      return {
        title: requirement.title || null,
        subtitle: requirement.subtitle || null,
        category: requirement.category || null,
        status: requirement.status || null,
        publicDocumentUrl: requirement.publicDocumentUrl || ''
      }
    })
    return db.collection('applicants')
      .doc(data.applicantId)
      .collection('public')
      .doc('requirements')
      .set({
        requirements: requirements
      })
  },
  ADD_APPLICANT_REQUIREMENT ({ commit }, payload) {
    return db.collection('applicants')
      .doc(payload.applicantId)
      .update({
        requirements: firebase.firestore.FieldValue.arrayUnion(payload.requirement),
        updatedAt: dayjs().format()
      })
  },
  UPDATE_APPLICANT_KEY_DATES ({ commit }, payload) {
    return db.collection('applicants')
      .doc(payload.applicantId)
      .update({
        keyDates: payload.keyDates,
        updatedAt: dayjs().format()
      })
  },
  UPDATE_APPLICANT_CHECKLIST ({ commit }, payload) {
    return db.collection('applicants')
      .doc(payload.applicantId)
      .update({
        contactChecklist: payload.contactChecklist,
        updatedAt: dayjs().format()
      })
  }
}

const getters = {
}

const mutations = {
  SET_APPLICANTS (state, payload) {
    state.applicants = payload
  },
  SET_APPLICANT (state, payload) {
    state.applicant = payload
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
